import EmailIcon from '@material-ui/icons/Email'
import { isSameDay, startOfToday } from 'date-fns'
import { utcToZonedTime } from 'date-fns-tz'
import { TextClamp } from 'shared/components/Base/Base'
import { Text } from 'shared/components/Text/Text'
import { useContainerBreakpoint } from 'shared/hooks/useBreakpoint'
import { breakpoints, breakpointsSize } from 'shared/theme/breakpoints'
import { darkGray, dividerGray, kellyGreenD1, lightGray1, midGray } from 'shared/theme/colors'
import styled, { css } from 'styled-components'

import { FlexBox } from '../../components/Base/FlexBox'
import { Pill } from '../../components/Pill/Pill'
import { isConsole } from '../../config/config'
import { getDateFormattedLong } from '../../helpers/formatters'
import { getLoadStopOrigin } from '../../helpers/job'
import { getJobConsoleStatusDetail, Job, JobStatusDetail } from '../../models/job'
import { LoadListItem } from '../Load/LoadListItem'

interface JobListItemProps {
  jobs: Job[]
  job: Job
  jobUrlConstructor?: (job: Job) => string | undefined
  index: number
  removeCardLayout?: boolean
  onOpen: (job: Job) => void
  renderAssignedTo: () => JSX.Element | null
  isInsideSidebar?: boolean
}

export function JobListItem({
  job,
  index,
  jobs,
  jobUrlConstructor,
  onOpen,
  renderAssignedTo,
  removeCardLayout,
  isInsideSidebar,
}: JobListItemProps) {
  const [containerRef, isMobile] = useContainerBreakpoint(breakpointsSize.md)
  const previousJob = jobs![index - 1]
  const jobOrigin = getLoadStopOrigin(job)
  const jobOriginDateFormatted = !!jobOrigin
    ? getDateFormattedLong(jobOrigin.dateStart, jobOrigin.locationTimezone)
    : ''

  const prevJobOrigin = getLoadStopOrigin(previousJob)
  const prevJobOriginDateFormatted = !!prevJobOrigin
    ? getDateFormattedLong(prevJobOrigin.dateStart, prevJobOrigin.locationTimezone)
    : ''
  const showJobId = isConsole() && job.humanId

  const showDateHeader =
    (!!jobOrigin && !!prevJobOrigin && jobOriginDateFormatted !== prevJobOriginDateFormatted) ||
    (!!jobOrigin && prevJobOrigin == null)

  const newDate = new Date(jobOrigin!.dateStart)
  // Just checking if the stop date is today isn't enough, isToday converts the stops date to the local timezone
  // need to check for the stop's local timezone, because we show that in the MultiStopPathItem, otherwise the date
  // will be off by a day but will show as today.
  // An example: Stop date is Aug 30, 00:00 CDT, user is Aug 29 10:00 PST,
  // the stop date will show as Aug 30, the header will be Aug 30, but will show as today,
  // because the stop date is Aug 29 22:00 PST
  const todaysDate =
    !!jobOrigin &&
    isSameDay(startOfToday(), utcToZonedTime(newDate.toISOString(), jobOrigin.locationTimezone))

  const showShipper = job.load.brokerIsShipper && job.isFactored

  // jobUrl will be undefined for EXTERNAL_BOOK or PENDING jobs, see JobListClickProvider
  const jobUrl = jobUrlConstructor?.(job)

  const renderItem = () => (
    <Container key={job.id} ref={containerRef} $isInsideSidebar={isInsideSidebar}>
      <div>
        {showDateHeader && !removeCardLayout && (
          <JobDateInfo todaysDate={todaysDate} dateFormatted={jobOriginDateFormatted} />
        )}
        {isInsideSidebar && showJobId && (
          <Text variant="uppercaseLabel3" fontColor={midGray} mb={1}>
            Job: {job.humanId}
          </Text>
        )}
        <LoadListItem
          key={job.id}
          load={job.load}
          confirmedRate={job.confirmedRate}
          handleOpen={isInsideSidebar || jobUrl ? undefined : () => onOpen(job)}
          highlight={job.statusDetail === JobStatusDetail.IN_PROGRESS}
          jobStatus={job.statusDetail}
          jobHumanStatus={job.humanStatusDetail}
          jobHumanStatusColor={job.humanStatusDetailColor}
          renderAssignedTo={renderAssignedTo}
          removeCardLayout={removeCardLayout}
          renderAdditionalTags={
            job.sentByEmail
              ? () => (
                  <FlexBox gap="5px" alignItems="center" styles="margin-top: auto;">
                    <StyledEmailIcon />
                    <Text variant="paragraph3" fontColor={lightGray1}>
                      Sent by email
                    </Text>
                  </FlexBox>
                )
              : undefined
          }
          isJob
        >
          <FlexBox isColumn gap="6px" justifyContent="space-between" isFullHeight>
            {!isInsideSidebar && showJobId && (
              <Text variant="uppercaseLabel3" fontColor={midGray}>
                Job: {job.humanId}
              </Text>
            )}

            {!isMobile && (
              <Text
                variant={isConsole() ? 'header3' : 'paragraph2'}
                fontColor={job.humanStatusDetailColor}
                fontWeight={600}
              >
                {isConsole() && job.statusDetail
                  ? getJobConsoleStatusDetail(job.statusDetail, job.humanStatusDetail)
                  : job.humanStatusDetail}
              </Text>
            )}

            <FlexBox alignItems="center" gap="5px" style={{ flexWrap: 'wrap' }}>
              {showShipper && (
                <Pill mt={-0.3} variant="small">
                  S
                </Pill>
              )}

              {job.load.brokerName && !isInsideSidebar && showJobId && showShipper && (
                <Text variant="paragraph2" fontColor={midGray} fontWeight={400}>
                  •
                </Text>
              )}

              {job.load.brokerName && (
                <TextClamp>
                  <Text
                    variant="paragraph2"
                    fontColor={midGray}
                    fontWeight={400}
                    styles="max-width: 265px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                  >
                    {job.load.brokerName}
                  </Text>
                </TextClamp>
              )}

              {job.load.brokerName && (job.load.sourceHumanId || job.load.brokerLoadId) && (
                <Text variant="paragraph2" fontColor={midGray} fontWeight={400}>
                  •
                </Text>
              )}

              {(job.load.sourceHumanId || job.load.brokerLoadId) && (
                <Text
                  variant="paragraph2"
                  fontColor={midGray}
                  fontWeight={400}
                  styles="max-width: 265px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis;"
                >
                  {job.load.sourceHumanId || job.load.brokerLoadId}
                </Text>
              )}
            </FlexBox>
          </FlexBox>
        </LoadListItem>
      </div>
    </Container>
  )

  // If the job is inside the sidebar, we don't want to make the whole card clickable, only the arrow
  // jobUrl will be undefined for EXTERNAL_BOOK or PENDING jobs, see JobListClickProvider
  if (!jobUrl || isInsideSidebar) {
    return renderItem()
  }

  return (
    <a href={jobUrl} style={{ textDecoration: 'none' }}>
      {renderItem()}
    </a>
  )
}

const Container = styled.div<{ $isInsideSidebar?: boolean }>`
  ${({ $isInsideSidebar }) =>
    $isInsideSidebar &&
    css`
      padding: 16px 0 16px 40px;
    `}
`

export const DateContainer = styled.div`
  padding-top: 24px;
  padding-bottom: 0;
  margin: auto;
`

export const GreenLabel = styled.span`
  font-size: 16px;
  line-height: 25px;
  font-weight: 600;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: ${kellyGreenD1};
  margin-left: 10px;

  @media ${breakpoints.xs} {
    font-size: 10px;
    line-height: 15px;
  }
`

export const StyledDate = styled.div<{ $todaysDate?: boolean }>`
  display: inline-block;
  font-size: 20px;
  line-height: 25px;
  font-weight: 600;
  padding-right: 5px;
  color: ${({ $todaysDate }) => ($todaysDate ? kellyGreenD1 : darkGray)};
`

const StyledEmailIcon = styled(EmailIcon)`
  color: ${dividerGray} !important;
  font-size: 16px !important;
  margin-top: -3px;
`

interface JobDateInfoProps {
  todaysDate: boolean
  dateFormatted: string
}

const JobDateInfo = ({ todaysDate, dateFormatted }: JobDateInfoProps) => {
  return (
    <DateContainer>
      <StyledDate $todaysDate={todaysDate}>{dateFormatted}</StyledDate>
      {todaysDate && <GreenLabel>Today</GreenLabel>}
    </DateContainer>
  )
}
